// `@/lib/share/og` — OG image rendering library.
//
// SCOPE: rendering Satori-based PNGs at `/api/og/<surface>/[id]/route.tsx`,
// the `<OgShell>` chrome, primitives, registry, fonts, scale tokens.
// Visual concerns only — pixels in the unfurl card.
//
// NOT in scope:
//   - `<meta>` tags / Twitter card / OpenGraph metadata → @/lib/seo
//   - JSON-LD structured data                            → @/lib/seo
//   - Robots / canonical / indexability policy           → @/lib/seo
//   - Per-page SEO data fetchers                         → @/lib/seo/surfaces
//
// Workspace: `/workspace` shows every variant + primitive side-by-side
// for live iteration. See app/[locale]/workspace/.

export type { OgUsdcMode } from '@/lib/currency/server';
// Edge-safe number formatters
export {
  formatOgAbbrev,
  formatOgAgeLong,
  formatOgAsOf,
  formatOgPercent,
  formatOgPercentMagnitude,
  formatOgPriceD9,
  formatOgUsdcD6,
} from '@/lib/currency/server';
// Public types
export type { CacheClass } from './cache';
// Avatar OG URL — honours the user's `avatarPreferences.rasterise`
// setting: rasterised pattern when `true` OR no upload, raw upload (via
// the custom Pinata gateway) when `false` AND upload is present.
export type { GetAvatarOgUrlArgs } from './get-avatar-og-url';
export { getAvatarOgUrl } from './get-avatar-og-url';
// Pattern rasteriser URL builder — every OG route routes user / market /
// outcome image sources through this helper so social unfurls render the
// formatted brand pattern instead of the raw upstream URL.
export type { GetPatternUrlOptions } from './get-pattern-url';
export { getPatternUrl } from './get-pattern-url';

// Outcome colour helpers (mirror the app's title-based palette)
export { getOutcomeColorOg, getOutcomeColorsOg } from './outcome-colors';
// Primitives — composable building blocks for OG route JSX.
export type { HandlePillProps } from './primitives/handle-pill';
export { HandlePill } from './primitives/handle-pill';
export type { OgAvatarProps, OgAvatarSize } from './primitives/og-avatar';
export { OgAvatar } from './primitives/og-avatar';
export {
  getOgAvatarInitials,
  getOgAvatarPaletteEntry,
  OG_AVATAR_PALETTE,
  resolveOgAvatarSeed,
} from './primitives/og-avatar-utils';
export type { OgClampProps, OgClampVariant } from './primitives/og-clamp';
export { OgClamp } from './primitives/og-clamp';
export type { OgDevErrorProps } from './primitives/og-dev-error';
export { OgDevError } from './primitives/og-dev-error';
export type { OgFundingBarProps, OgFundingBarTone } from './primitives/og-funding-bar';
export { OgFundingBar } from './primitives/og-funding-bar';
export type { OgGlobeProps } from './primitives/og-globe';
export { OgGlobe } from './primitives/og-globe';
export type { OgImageProps } from './primitives/og-image';
export { OgImage } from './primitives/og-image';
export { isOgRenderableSrc } from './primitives/og-image-src';
export type { OgLogomarkProps } from './primitives/og-logomark';
export { OgLogomark } from './primitives/og-logomark';
export type { OgOutcomeChip, OgOutcomeChipsProps } from './primitives/og-outcome-chips';
export { OgOutcomeChips } from './primitives/og-outcome-chips';
export {
  OgBody,
  OgEditorialTitle,
  OgEyebrow,
  OgHeading,
  OgKpi,
} from './primitives/og-typography';
export { OgValueChange } from './primitives/og-value-change';
export type { OutcomeRowProps } from './primitives/outcome-row';
export { OutcomeRow } from './primitives/outcome-row';
export type { PnlChartProps, PnlPoint } from './primitives/pnl-chart';
export { PnlChart } from './primitives/pnl-chart';
export type { ProbabilityBarProps, ProbabilitySegment } from './primitives/probability-bar';
export { ProbabilityBar } from './primitives/probability-bar';
export type { StatBlockProps } from './primitives/stat-block';
export { StatBlock } from './primitives/stat-block';
// Registry types — `OgShellVariant` is referenced by render code; the
// descriptor type is referenced by the seo lib (cross-tree dependency
// is intentional: the OG image URL builder lives here, the metadata
// builder over in @/lib/seo imports both).
export type { OgShellVariant, OgSurfaceDescriptor } from './registry';
// Thumbnail-safe scale tokens + safe-area helpers
export {
  isThumbnailSafe,
  OG_BODY_PX,
  OG_FEED_CROP_RATIO,
  OG_HEADING_PX,
  OG_HERO_PX,
  OG_LABEL_PX,
  OG_MIN_LEGIBLE_PX,
  OG_SAFE_AREA_PX,
  ogSafeAreaInsets,
} from './scale';
// Shell — dynamic chrome wrapping every OG image
export type { OgShellProps } from './shell/og-shell';
export { OgShell } from './shell/og-shell';
// Tokens (referenced from route-level JSX for positive/negative tints etc.)
export type { OgToken } from './tokens';
export { OG_CONTENT_WIDTH, OG_DIMENSIONS, OG_SHELL_INSET_PX, OG_TOKENS } from './tokens';
// URL builder — used by @/lib/seo and by share components rendering previews.
export { getOgImageUrl } from './url';

// `renderOg` is Edge-runtime-only — route handlers import it directly
// from `@/lib/share/og/render` so this barrel stays client-safe (the
// workspace preview is a client component).
